import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SelectedFilters } from 'components';
import { goalTypes } from 'fragments';
import { goalDataTypes } from 'utils';
import { libraryTabEnums } from './constants';

/**
 * Active-filter chips for all three Library tabs.
 *
 * One component rather than one per tab: Goals, Targets and Options each have their own table head,
 * so the column filters differ, but they share the name search and write to the same
 * `location.state`. Gating each descriptor with `show` keeps a chip from claiming a filter the
 * visible tab does not apply, while "Clear Filters" still clears every key — otherwise a filter
 * hidden on this tab survives invisibly into the one that does apply it.
 *
 * Note the Options tab's Data Collection Method column writes `dataType` (singular) while Goals and
 * Targets write `dataTypes`. Separate keys, so separate descriptors.
 *
 * `GetGoalsDTO` also accepts `teachingMethods`, but no column offers it and there is no filter
 * popover on this page, so nothing sets it — a chip for it could never appear and it is not
 * declared here.
 */

/** `[{ id, label }]` lists resolve through `options`, which reads `name`. */
const byLabel = (list) => list?.map((i) => ({ id: i.id, name: i.label }));

export const LibrarySelectedFilters = () => {
    const location = useLocation();
    const info = location?.state;
    const domainList = useSelector((state) => state.domains.domainList);
    const tagList = useSelector((state) => state.tags.tagList);

    const isGoals = info?.tabType ? info?.tabType === libraryTabEnums.GOALS : true;
    const isTargets = info?.tabType === libraryTabEnums.TARGETS;
    const isOptions = info?.tabType === libraryTabEnums.OPTIONS;

    const filtersList = [
        // Column-header filters, per tab.
        { name: 'Domain', key: 'domains', options: domainList, loading: !domainList?.length, show: isGoals },
        { name: 'Type', key: 'goalType', options: byLabel(goalTypes), show: isGoals || isTargets },
        { name: 'Data Method', key: 'dataTypes', options: byLabel(goalDataTypes), show: isGoals || isTargets },
        { name: 'Data Method', key: 'dataType', options: byLabel(goalDataTypes), show: isOptions },
        { name: 'Tag', key: 'tags', options: tagList, loading: !tagList?.length, show: isGoals },
        { name: 'Search', key: 'searchValue', companions: ['searchKey'] },
    ];

    return <SelectedFilters filtersList={filtersList} doNotCrop />;
};
